import { Deletable } from '../interface/deletable'
import { Factory } from '../interface/factory'
import { PaymentTerminalComponentType } from './locker'
import { Settings } from './settings'
import { EquipmentSnapshot, Recipient } from './watchdog'
import {
  applyEntitySchema,
  objectSchema,
  optionalTolerant,
  parseEntity,
  sourceJson,
  wireValue,
} from '../../_utils/fcdc-v2'
import * as z from 'zod'

const sourceSchema = objectSchema({
  type: optionalTolerant(z.string()),
  id: optionalTolerant(z.string()),
})

const entrySchema = objectSchema({
  id: optionalTolerant(z.string()),
  source: optionalTolerant(z.unknown()),
  created: optionalTolerant(z.union([z.string(), z.number()])),
  type: optionalTolerant(z.string()),
  deleted: optionalTolerant(z.boolean()),
  pk: optionalTolerant(z.string()),
})

export class Source {
  type: SourceType
  id?: string
  constructor(json: any) {
    const source = sourceJson(json) as any
    this.type = source.type
    this.id = source.id
    applyEntitySchema(this, sourceSchema, source, {})
  }
}

export enum SourceType {
  System = 'System',
  User = 'User',
}

export enum EntryType {
  SiteCreated = 'SiteCreated',
  SiteUpdated = 'SiteUpdated',
  SiteDeleted = 'SiteDeleted',
  LocationCreated = 'LocationCreated',
  LocationUpdated = 'LocationUpdated',
  LocationDeleted = 'LocationDeleted',
  ColumnCreated = 'ColumnCreated',
  ColumnUpdated = 'ColumnUpdated',
  ColumnDeleted = 'ColumnDeleted',
  ColumnHardwareIdAssigned = 'ColumnHardwareIdAssigned',
  ColumnHardwareIdCleared = 'ColumnHardwareIdCleared',
  ColumnHardwareContactEstablished = 'ColumnHardwareContactEstablished',
  ColumnHardwareContactLost = 'ColumnHardwareContactLost',
  PaymentTerminalHardwareContactEstablished =
    'PaymentTerminalHardwareContactEstablished',
  PaymentTerminalHardwareContactLost = 'PaymentTerminalHardwareContactLost',
  LockerUpdated = 'LockerUpdated',
  LockerUnlockCommandCreated = 'LockerUnlockCommandCreated',
  LockerClosed = 'LockerClosed',
  LockerOpened = 'LockerOpened',
  LockerLookupCreated = 'LockerLookupCreated',
  SessionHolderSmsSent = 'SessionHolderSmsSent',
  SessionHolderEmailSent = 'SessionHolderEmailSent',
  SessionConfirmPriceDiscrepancy = 'SessionConfirmPriceDiscrepancy',
  SessionExtendPriceDiscrepancy = 'SessionExtendPriceDiscrepancy',
  SessionTerminatePriceDiscrepancy = 'SessionTerminatePriceDiscrepancy',
  SessionTerminateRefundDiscrepancy = 'SessionTerminateRefundDiscrepancy',
  PaymentTerminalHardwareIdAssigned = 'PaymentTerminalHardwareIdAssigned',
  PaymentTerminalHardwareIdCleared = 'PaymentTerminalHardwareIdCleared',
  PaymentTerminalTypeChanged = 'PaymentTerminalTypeChanged',
  PaymentTerminalHardwareIdsSwapped = 'PaymentTerminalHardwareIdsSwapped',
  PaymentTerminalLockerAdded = 'PaymentTerminalLockerAdded',
  PaymentTerminalLockerDeleted = 'PaymentTerminalLockerDeleted',
  PaymentTerminalLockersSet = 'PaymentTerminalLockersSet',
  PaymentTerminalLockersCleared = 'PaymentTerminalLockersCleared',
  PspConfigIssue = 'PspConfigIssue',
  BookingLockerAllocationSucceeded = 'BookingLockerAllocationSucceeded',
  BookingLockerAllocationFailed = 'BookingLockerAllocationFailed',
  CountryCreated = 'CountryCreated',
  CountryUpdated = 'CountryUpdated',
  CountryDeleted = 'CountryDeleted',
  CustomerCreated = 'CustomerCreated',
  CustomerUpdated = 'CustomerUpdated',
  CustomerDeleted = 'CustomerDeleted',
  PresenceCreated = 'PresenceCreated',
  PresenceUpdated = 'PresenceUpdated',
  PresenceDeleted = 'PresenceDeleted',
  WatchdogOfflineAlertSent = 'WatchdogOfflineAlertSent',
  WatchdogRecoveryAlertSent = 'WatchdogRecoveryAlertSent',
}

export class Entry implements Deletable {
  id: string
  source: Source
  created: Date
  type: EntryType
  deleted: boolean
  pk: string

  constructor(json: any) {
    const source = sourceJson(json) as any
    this.id = source.id
    this.source = source.source
    this.created = new Date(source.created)
    this.type = source.type
    this.deleted = false
    this.pk = source.pk
    applyEntitySchema(this, entrySchema, wireValue(source), {})
  }

  static getFactory(): Factory<Entry> {
    return new (class implements Factory<Entry> {
      make(json: any): Entry {
        switch (json.type) {
          case EntryType.SiteCreated:
            return new SiteCreated(json)
          case EntryType.SiteUpdated:
            return new SiteUpdated(json)
          case EntryType.SiteDeleted:
            return new SiteDeleted(json)
          case EntryType.LocationCreated:
            return new LocationCreated(json)
          case EntryType.LocationUpdated:
            return new LocationUpdated(json)
          case EntryType.LocationDeleted:
            return new LocationDeleted(json)
          case EntryType.ColumnCreated:
            return new ColumnCreated(json)
          case EntryType.ColumnUpdated:
            return new ColumnUpdated(json)
          case EntryType.ColumnDeleted:
            return new ColumnDeleted(json)
          case EntryType.ColumnHardwareIdAssigned:
            return new ColumnHardwareIdAssigned(json)
          case EntryType.ColumnHardwareIdCleared:
            return new ColumnHardwareIdCleared(json)
          case EntryType.ColumnHardwareContactEstablished:
            return new ColumnHardwareContactEstablished(json)
          case EntryType.ColumnHardwareContactLost:
            return new ColumnHardwareContactLost(json)
          case EntryType.PaymentTerminalHardwareContactEstablished:
            return new PaymentTerminalHardwareContactEstablished(json)
          case EntryType.PaymentTerminalHardwareContactLost:
            return new PaymentTerminalHardwareContactLost(json)
          case EntryType.LockerUpdated:
            return new LockerUpdated(json)
          case EntryType.LockerClosed:
            return new LockerClosed(json)
          case EntryType.LockerOpened:
            return new LockerOpened(json)
          case EntryType.LockerLookupCreated:
            return new LockerLookupCreated(json)
          case EntryType.LockerUnlockCommandCreated:
            return new LockerUnlockCommandCreated(json)
          case EntryType.SessionConfirmPriceDiscrepancy:
            return new SessionConfirmPriceDiscrepancy(json)
          case EntryType.SessionExtendPriceDiscrepancy:
            return new SessionExtendPriceDiscrepancy(json)
          case EntryType.SessionTerminatePriceDiscrepancy:
            return new SessionTerminatePriceDiscrepancy(json)
          case EntryType.SessionTerminateRefundDiscrepancy:
            return new SessionTerminateRefundDiscrepancy(json)
          case EntryType.SessionHolderSmsSent:
            return new SessionHolderSmsSent(json)
          case EntryType.SessionHolderEmailSent:
            return new SessionHolderEmailSent(json)
          case EntryType.PaymentTerminalHardwareIdCleared:
            return new PaymentTerminalHardwareIdCleared(json)
          case EntryType.PaymentTerminalHardwareIdAssigned:
            return new PaymentTerminalHardwareIdAssigned(json)
          case EntryType.PaymentTerminalTypeChanged:
            return new PaymentTerminalTypeChanged(json)
          case EntryType.PaymentTerminalHardwareIdsSwapped:
            return new PaymentTerminalHardwareIdsSwapped(json)
          case EntryType.PaymentTerminalLockerAdded:
            return new PaymentTerminalLockerAdded(json)
          case EntryType.PaymentTerminalLockerDeleted:
            return new PaymentTerminalLockerDeleted(json)
          case EntryType.PaymentTerminalLockersSet:
            return new PaymentTerminalLockersSet(json)
          case EntryType.PaymentTerminalLockersCleared:
            return new PaymentTerminalLockersCleared(json)
          case EntryType.PspConfigIssue:
            return new PspConfigIssue(json)
          case EntryType.BookingLockerAllocationSucceeded:
            return new BookingLockerAllocationSucceeded(json)
          case EntryType.BookingLockerAllocationFailed:
            return new BookingLockerAllocationFailed(json)
          case EntryType.CountryCreated:
            return new CountryCreated(json)
          case EntryType.CountryUpdated:
            return new CountryUpdated(json)
          case EntryType.CountryDeleted:
            return new CountryDeleted(json)
          case EntryType.CustomerCreated:
            return new CustomerCreated(json)
          case EntryType.CustomerUpdated:
            return new CustomerUpdated(json)
          case EntryType.CustomerDeleted:
            return new CustomerDeleted(json)
          case EntryType.PresenceCreated:
            return new PresenceCreated(json)
          case EntryType.PresenceUpdated:
            return new PresenceUpdated(json)
          case EntryType.PresenceDeleted:
            return new PresenceDeleted(json)
          case EntryType.WatchdogOfflineAlertSent:
            return new WatchdogOfflineAlertSent(json)
          case EntryType.WatchdogRecoveryAlertSent:
            return new WatchdogRecoveryAlertSent(json)
          default:
            return parseEntity(entrySchema, json ?? {}) as unknown as Entry
        }
      }

      getTableName(): string {
        return 'log_entries'
      }
    })()
  }
}

export class CountryCreated extends Entry {
  countryCode: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.countryCode = json.countryCode
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class CountryUpdated extends Entry {
  countryCode: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.countryCode = json.countryCode
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class CountryDeleted extends Entry {
  countryCode: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.countryCode = json.countryCode
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class CustomerCreated extends Entry {
  customerId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.customerId = json.customerId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class CustomerUpdated extends Entry {
  customerId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.customerId = json.customerId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class CustomerDeleted extends Entry {
  customerId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.customerId = json.customerId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class PresenceCreated extends Entry {
  countryCode: string
  customerId: string
  presenceId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.countryCode = json.countryCode
    this.customerId = json.customerId
    this.presenceId = json.presenceId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class PresenceUpdated extends Entry {
  countryCode: string
  customerId: string
  presenceId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.countryCode = json.countryCode
    this.customerId = json.customerId
    this.presenceId = json.presenceId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class PresenceDeleted extends Entry {
  countryCode: string
  customerId: string
  presenceId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.countryCode = json.countryCode
    this.customerId = json.customerId
    this.presenceId = json.presenceId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class SiteCreated extends Entry {
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class SiteUpdated extends Entry {
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class SiteDeleted extends Entry {
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class LocationCreated extends Entry {
  locationId: string
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.locationId = json.locationId
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class LocationUpdated extends Entry {
  locationId: string
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.locationId = json.locationId
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class LocationDeleted extends Entry {
  locationId: string
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.locationId = json.locationId
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class ColumnCreated extends Entry {
  columnId: string
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.columnId = json.columnId
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class ColumnUpdated extends Entry {
  columnId: string
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.columnId = json.columnId
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class ColumnDeleted extends Entry {
  columnId: string
  siteId: string
  settings?: Settings

  constructor(json: any) {
    super(json)
    this.columnId = json.columnId
    this.siteId = json.siteId
    this.settings = json.settings ? new Settings(json.settings) : undefined
  }
}

export class ColumnHardwareIdAssigned extends Entry {
  columnId: string
  hardwareId: string

  constructor(json: any) {
    super(json)
    this.columnId = json.columnId
    this.hardwareId = json.hardwareId
  }
}

export class ColumnHardwareIdCleared extends Entry {
  columnId: string
  hardwareId: string

  constructor(json: any) {
    super(json)
    this.columnId = json.columnId
    this.hardwareId = json.hardwareId
  }
}

export class ColumnHardwareContactEstablished extends Entry {
  columnId: string
  hardwareId: string
  firmwareId?: string

  constructor(json: any) {
    super(json)
    this.columnId = json.columnId
    this.hardwareId = json.hardwareId
    this.firmwareId = json.firmwareId ?? undefined
  }
}

export class ColumnHardwareContactLost extends Entry {
  columnId: string
  hardwareId: string
  firmwareId?: string
  discoveredAt?: Date
  lastHardwareContact?: Date

  constructor(json: any) {
    super(json)
    this.columnId = json.columnId
    this.hardwareId = json.hardwareId
    this.firmwareId = json.firmwareId ?? undefined
    this.discoveredAt = json.discoveredAt
      ? new Date(json.discoveredAt)
      : undefined
    this.lastHardwareContact = json.lastHardwareContact
      ? new Date(json.lastHardwareContact)
      : undefined
  }
}

export class PaymentTerminalHardwareContactEstablished extends Entry {
  siteId: string
  columnId: string
  lockerId: string
  identityHash: string
  componentType: PaymentTerminalComponentType
  deviceId: string
  firmwareVersion: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.identityHash = json.identityHash
    this.componentType = json.componentType as PaymentTerminalComponentType
    this.deviceId = json.deviceId
    this.firmwareVersion = json.firmwareVersion
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalHardwareContactLost extends Entry {
  siteId: string
  columnId: string
  lockerId: string
  identityHash: string
  componentType: PaymentTerminalComponentType
  deviceId: string
  firmwareVersion: string
  discoveredAt?: Date
  lastHardwareContact?: Date

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.identityHash = json.identityHash
    this.componentType = json.componentType as PaymentTerminalComponentType
    this.deviceId = json.deviceId
    this.firmwareVersion = json.firmwareVersion
    this.discoveredAt = json.discoveredAt
      ? new Date(json.discoveredAt)
      : undefined
    this.lastHardwareContact = json.lastHardwareContact
      ? new Date(json.lastHardwareContact)
      : undefined
  }

  getColumnId() {
    return this.columnId
  }
}

export class LockerUpdated extends Entry {
  lockerId: string

  constructor(json: any) {
    super(json)
    this.lockerId = json.lockerId
  }

  getColumnId() {
    return this.lockerId.split('_')[0]
  }
}

export class LockerUnlockCommandCreated extends Entry {
  lockerId: string
  sessionId?: string

  constructor(json: any) {
    super(json)
    this.lockerId = json.lockerId
    this.sessionId = json.sessionId
  }

  getColumnId() {
    return this.lockerId.split('_')[0]
  }
}
export class LockerLookupCreated extends Entry {
  lockerId: string

  constructor(json: any) {
    super(json)
    this.lockerId = json.lockerId
  }

  getColumnId() {
    return this.lockerId.split('_')[0]
  }
}

export class LockerClosed extends Entry {
  lockerId: string
  sessionId?: string

  constructor(json: any) {
    super(json)
    this.lockerId = json.lockerId
    this.sessionId = json.sessionId
  }

  getColumnId() {
    return this.lockerId.split('_')[0]
  }
}

export class LockerOpened extends Entry {
  lockerId: string
  sessionId?: string

  constructor(json: any) {
    super(json)
    this.lockerId = json.lockerId
    this.sessionId = json.sessionId
  }

  getColumnId() {
    return this.lockerId.split('_')[0]
  }
}

export class SessionHolderSmsSent extends Entry {
  sessionId: string
  userId: string
  phoneNumber: string

  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.userId = json.userId
    this.phoneNumber = json.phoneNumber
  }
}
export class SessionHolderEmailSent extends Entry {
  sessionId: string
  userId: string
  emailAddress: string

  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.userId = json.userId
    this.emailAddress = json.emailAddress
  }
}

export class SessionConfirmPriceDiscrepancy extends Entry {
  sessionId: string
  siteId: string
  apiTotal: number
  appTotal: number
  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.siteId = json.siteId
    this.apiTotal = json.apiTotal || 0
    this.appTotal = json.appTotal || 0
  }
}

export class SessionExtendPriceDiscrepancy extends Entry {
  sessionId: string
  siteId: string
  apiTotal: number
  appTotal: number
  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.siteId = json.siteId
    this.apiTotal = json.apiTotal || 0
    this.appTotal = json.appTotal || 0
  }
}

export class SessionTerminatePriceDiscrepancy extends Entry {
  sessionId: string
  siteId: string
  apiTotal: number
  appTotal: number
  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.siteId = json.siteId
    this.apiTotal = json.apiTotal || 0
    this.appTotal = json.appTotal || 0
  }
}

export class SessionTerminateRefundDiscrepancy extends Entry {
  sessionId: string
  siteId: string
  apiTotal: number
  appTotal: number
  currency: string
  usedUntil: Date

  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.siteId = json.siteId
    this.apiTotal = json.apiTotal || 0
    this.appTotal = json.appTotal || 0
    this.currency = json.currency
    this.usedUntil = new Date(json.usedUntil)
  }
}

export class BookingLockerAllocationFailed extends Entry {
  sessionId: string
  desiredPriceCategoryId: string
  siteId: string

  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.desiredPriceCategoryId = json.desiredPriceCategoryId
    this.siteId = json.siteId
  }
}

export class PspConfigIssue extends Entry {
  merchantAccount: string
  sessionId: string
  siteId: string

  constructor(json: any) {
    super(json)
    this.merchantAccount = json.merchantAccount
    this.sessionId = json.sessionId
    this.siteId = json.siteId
  }
}

export class PaymentTerminalHardwareIdAssigned extends Entry {
  siteId: string
  columnId: string
  lockerId: string
  hardwareId: string
  tabletDeviceId?: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.hardwareId = json.hardwareId
    this.tabletDeviceId = json.tabletDeviceId ?? undefined
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalHardwareIdCleared extends Entry {
  siteId: string
  columnId: string
  lockerId: string
  hardwareId: string
  tabletDeviceId?: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.hardwareId = json.hardwareId
    this.tabletDeviceId = json.tabletDeviceId ?? undefined
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalLockerAdded extends Entry {
  siteId: string
  columnId: string
  terminalLockerId: string
  addedLockerId: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.terminalLockerId = json.terminalLockerId
    this.addedLockerId = json.addedLockerId
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalLockerDeleted extends Entry {
  siteId: string
  columnId: string
  terminalLockerId: string
  deletedLockerId: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.terminalLockerId = json.terminalLockerId
    this.deletedLockerId = json.deletedLockerId
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalLockersSet extends Entry {
  siteId: string
  columnId: string
  terminalLockerId: string
  newLockerIds: string[]

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.terminalLockerId = json.terminalLockerId
    this.newLockerIds = json.newLockerIds
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalLockersCleared extends Entry {
  siteId: string
  columnId: string
  terminalLockerId: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.terminalLockerId = json.terminalLockerId
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalTypeChanged extends Entry {
  siteId: string
  columnId: string
  lockerId: string
  oldType: string
  newType: string
  oldHardwareId: string
  newHardwareId: string
  oldTabletDeviceId?: string
  newTabletDeviceId?: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.oldType = json.oldType
    this.newType = json.newType
    this.oldHardwareId = json.oldHardwareId
    this.newHardwareId = json.newHardwareId
    this.oldTabletDeviceId = json.oldTabletDeviceId
    this.newTabletDeviceId = json.newTabletDeviceId
  }

  getColumnId() {
    return this.columnId
  }
}

export class PaymentTerminalHardwareIdsSwapped extends Entry {
  siteId: string
  columnId: string
  lockerId: string
  terminalType: string
  oldHardwareId: string
  newHardwareId: string
  oldTabletDeviceId?: string
  newTabletDeviceId?: string

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.columnId = json.columnId
    this.lockerId = json.lockerId
    this.terminalType = json.terminalType
    this.oldHardwareId = json.oldHardwareId
    this.newHardwareId = json.newHardwareId
    this.oldTabletDeviceId = json.oldTabletDeviceId
    this.newTabletDeviceId = json.newTabletDeviceId
  }

  getColumnId() {
    return this.columnId
  }
}

export class BookingLockerAllocationSucceeded extends Entry {
  sessionId: string
  desiredPriceCategoryId: string
  siteId: string

  constructor(json: any) {
    super(json)
    this.sessionId = json.sessionId
    this.desiredPriceCategoryId = json.desiredPriceCategoryId
    this.siteId = json.siteId
  }
}

export class WatchdogOfflineAlertSent extends Entry {
  siteId: string
  locationId: string
  equipmentSnapshots: EquipmentSnapshot[]
  recipients: Recipient[]

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.locationId = json.locationId
    this.equipmentSnapshots = json.equipmentSnapshots
      ? json.equipmentSnapshots.map((snapshot: any) =>
          EquipmentSnapshot.fromJson(snapshot)
        )
      : []
    this.recipients = json.recipients
      ? json.recipients.map((recipient: any) => Recipient.fromJson(recipient))
      : []
  }
}

export class WatchdogRecoveryAlertSent extends Entry {
  siteId: string
  locationId: string
  recoveredEquipmentSnapshots: EquipmentSnapshot[]
  recipients: Recipient[]

  constructor(json: any) {
    super(json)
    this.siteId = json.siteId
    this.locationId = json.locationId
    this.recoveredEquipmentSnapshots = json.recoveredEquipmentSnapshots
      ? json.recoveredEquipmentSnapshots.map((snapshot: any) =>
          EquipmentSnapshot.fromJson(snapshot)
        )
      : []
    this.recipients = json.recipients
      ? json.recipients.map((recipient: any) => Recipient.fromJson(recipient))
      : []
  }
}

export type ColumnEntry =
  | ColumnCreated
  | ColumnUpdated
  | ColumnDeleted
  | ColumnHardwareIdAssigned
  | ColumnHardwareIdCleared
  | ColumnHardwareContactEstablished
  | ColumnHardwareContactLost
  | PaymentTerminalHardwareContactEstablished
  | PaymentTerminalHardwareContactLost
  | LockerUpdated
  | LockerUnlockCommandCreated
  | LockerLookupCreated
  | LockerClosed
  | LockerOpened
  | PaymentTerminalHardwareIdAssigned
  | PaymentTerminalHardwareIdCleared
  | PaymentTerminalTypeChanged
  | PaymentTerminalHardwareIdsSwapped
  | PaymentTerminalLockerAdded
  | PaymentTerminalLockerDeleted
  | PaymentTerminalLockersSet
  | PaymentTerminalLockersCleared
